import { combineReducers } from 'redux';
import { ADVERT_FORM } from 'shared/constants/actionTypes';
import currency from 'shared/constants/json/currency.json';
import mileageUnits from 'shared/constants/json/mileageUnits.json';
import { arrayMove } from 'react-sortable-hoc';
import capacityUnits from 'shared/constants/json/capacityUnits.json';
import powerUnits from 'shared/constants/json/powerUnits.json';
import engineFeatures from 'shared/constants/json/engineFeatures.json';
import statuses from 'shared/constants/json/statuses.json';
import { parseDecimal } from 'shared/helpers/advertForm';
import { FORM_STEPS } from 'shared/constants/advertForm';

const ADVERT_FORM_SELECTED = 'ADVERT_FORM__SELECTED__';

const immutablySetField = (state, field, value) => ({
  ...state,
  [field]: value,
});
const immutablySetDecimalField = (state, field, value, intLength, decimalLength) =>
  immutablySetField(state, field, parseDecimal(value, intLength, decimalLength));

const arrayToPicked = array => array.reduce((object, field) => ({ ...object, [field]: true }), {});

const initialState = {
  selected: {
    id: 0,
    active: 0,
    transport: null,
    make: null,
    model: null,
    year: null,
    series: null,
    engine: null,
    drive: null,
    gearbox: null,
    modification: null,
    equipment: null,
    color: null,
    mileage: null,
    vin: null,
    price: null,
    generation: null,
    description: null,
    location: null,
    contactName: null,
    email: null,
    photos: [],
    phones: [],
    colorFeatures: {},
    options: {},
    advertFields: {},
    mileageUnit: mileageUnits[0],
    currency: currency[1],
    abroad: false,
    drivenFrom: null,
    video: null,
    regNumber: '',
    usedWarranty: null,
    // Moto
    category: null,
    engineOrder: null,
    tacts: null,
    capacity: null,
    capacityUnit: null,
    power: null,
    powerUnit: null,
    // Truck
    cabin: null,
    unloading: null,
    ecotype: null,
    cabinSuspension: null,
    chassisSuspension: null,
    // Bus
    seats: null,
    // Technics
    tonnage: null,
    capacityBody: null,
    axises: null,
    dimensionWidth: null,
    dimensionHeight: null,
    dimensionLength: null,
    dimensionWeight: null,
    capacityBucket: null,
    layingWidth: null,
    liftHeight: null,
    admissionHeight: null,
    armBoom: null,
    depBig: null,
    productivity: null,
    // Trailer
    capacityBodyType: capacityUnits[0],
    suspension: null,
    brakes: null,
    //
    clarifyPrice: false,
    clarifyPriceLink: '',
    //
    modelTitle: null,
    makeTitle: null,
    // Water
    overallLength: null,
    dryWeight: null,
    draft: null,
    displacement: null,
    fuelTank: null,
    waterTank: null,
    seaworthinessClass: null,
  },
  data: {
    models: null,
    modifications: null,
    equipments: null,
    generations: null,
    existingPhotos: [],
    newExistingPhotos: [],
    years: null,
    series: null,
    body: null,
    averagePrice: undefined,
    advertsAveragePrice: null,
    averagePriceParams: null,
    showAveragePrice: false,
    warranty: null,
    newAdvert: {},
    currentFormStep: FORM_STEPS.MAIN,
  },
  vocabulary: {
    statuses,
    currency,
    mileageUnits,
    capacityUnits,
    powerUnits,
    engineFeatures,
    // avto
    europeanCountries: null,
    //
    makes: null,
    colors: null,
    colorFeatures: null,
    options: null,
    priceStatuses: null,
    locations: null,
    services: null,
    // Moto
    categories: null,
    engines: null,
    engineOrders: null,
    drives: null,
    gearboxes: null,
    tacts: null,
    // Trucks
    cabins: null,
    unloadings: null,
    ecotypes: null,
    cabinSuspensions: null,
    chassisSuspensions: null,
    // Trailers
    suspensions: null,
    brakes: null,
    // Water
    overallLength: null,
    dryWeight: null,
    draft: null,
    displacement: null,
    fuelTank: null,
    waterTank: null,
    seaworthinessClasses: null,
  },
  isEdit: null,
  isClearBtn: false, // - for show/hide clear button in header
};

const data = (state = initialState.data, action) => {
  switch (action.type) {
    case ADVERT_FORM.CLEAR:
      return initialState.data;

    case ADVERT_FORM.DATA_MODELS_SET:
      return immutablySetField(state, 'models', action.payload);

    case ADVERT_FORM.DATA_MODIFICATIONS_SET:
      return immutablySetField(state, 'modifications', action.payload);

    case ADVERT_FORM.DATA_EQUIPMENTS_SET:
      return immutablySetField(state, 'equipments', action.payload);

    case ADVERT_FORM.DATA_EXISTING_PHOTOS_SET:
      return immutablySetField(state, 'existingPhotos', action.payload);

    case ADVERT_FORM.DATA_EXISTING_PHOTOS_SET_NEW:
      if ('set' in action.payload) {
        const len = state.newExistingPhotos.length;
        return immutablySetField(state, 'newExistingPhotos', [
          ...state.newExistingPhotos,
          {
            image: action.payload.set,
            order: len + 1,
            total: len,
          },
        ]);
      }
      if ('rewrite' in action.payload) {
        return immutablySetField(state, 'newExistingPhotos', action.payload.rewrite);
      }
      if ('delete' in action.payload) {
        const newExistingPhotos = state.newExistingPhotos;
        newExistingPhotos.splice(action.payload.delete, 1);
        return immutablySetField(state, 'newExistingPhotos', newExistingPhotos);
      }
      if ('sort' in action.payload) {
        const sort = action.payload.sort;
        const sortingPhotos = arrayMove(state.newExistingPhotos, sort.oldIndex, sort.newIndex);

        for (let a = 0; a < sortingPhotos.length; a += 1) {
          sortingPhotos[a].order = a;
        }
        return immutablySetField(state, 'newExistingPhotos', sortingPhotos);
      }
      if ('clear' in action.payload) {
        return immutablySetField(state, 'newExistingPhotos', []);
      }
      return state;

    case ADVERT_FORM.DATA_EXISTING_ADVERTS_SET_NEW:
      if ('set' in action.payload) {
        return immutablySetField(state, 'newExistingAdverts', action.payload.set);
      }
      if ('clear' in action.payload) {
        return immutablySetField(state, 'newExistingAdverts', {});
      }
      return state;

    case ADVERT_FORM.DATA_NEW_ADVERT:
      if ('set' in action.payload) {
        return immutablySetField(state, 'newAdvert', action.payload.set);
      }
      if ('clear' in action.payload) {
        return immutablySetField(state, 'newAdvert', null);
      }
      return state;

    case ADVERT_FORM.DATA_CURRENT_FORM_STEP:
      if ('set' in action.payload) {
        return immutablySetField(state, 'currentFormStep', action.payload.set);
      }
      if ('clear' in action.payload) {
        return immutablySetField(state, 'currentFormStep', '');
      }
      return state;

    case ADVERT_FORM.DATA_YEARS_SET:
      return immutablySetField(state, 'years', action.payload);

    case ADVERT_FORM.DATA_GENERATIONS_SET:
      return immutablySetField(state, 'generations', action.payload);

    case ADVERT_FORM.DATA_SERIES_SET:
      return immutablySetField(state, 'series', action.payload);

    case ADVERT_FORM.DATA_AVERAGE_PRICE_SET:
      return immutablySetField(state, 'averagePrice', action.payload);

    case ADVERT_FORM.DATA_ADVERTS_AVERAGE_PRICE_SET:
      return immutablySetField(state, 'advertsAveragePrice', action.payload);

    case ADVERT_FORM.DATA_AVERAGE_PRICE_PARAMS_SET:
      return immutablySetField(state, 'averagePriceParams', action.payload);

    case ADVERT_FORM.DATA_WARRANTY_SET:
      return immutablySetField(state, 'warranty', action.payload);

    case ADVERT_FORM.DATA_SHOW_AVERAGE_PRICE_SET:
      return immutablySetField(state, 'showAveragePrice', action.payload);

    default:
      return state;
  }
};

const vocabulary = (state = initialState.vocabulary, action) => {
  switch (action.type) {
    case ADVERT_FORM.VOCABULARY_SET: {
      return { ...state, ...action.payload };
    }

    case ADVERT_FORM.VOCABULARY_CLEAR: {
      return initialState.vocabulary;
    }

    default:
      return state;
  }
};

const selected = (state = initialState.selected, action) => {
  switch (action.type) {
    case ADVERT_FORM.CLEAR: {
      return {
        ...initialState.selected,
        transport: state.transport,
        phones: state.phones,
      };
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__ID}`: {
      return immutablySetField(state, 'id', action.payload);
    }
    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__ACTIVE}`: {
      return immutablySetField(state, 'active', action.payload);
    }
    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__TRANSPORT}`: {
      return immutablySetField(state, 'transport', action.payload);
    }
    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__MAKE}`: {
      return immutablySetField(state, 'make', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__MODEL}`: {
      return immutablySetField(state, 'model', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__YEAR}`: {
      return immutablySetField(state, 'year', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__GENERATION}`: {
      return immutablySetField(state, 'generation', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__SERIES}`: {
      return immutablySetField(state, 'series', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__BODY}`: {
      return immutablySetField(state, 'body', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__MILEAGE_UNIT}`: {
      return immutablySetField(state, 'mileageUnit', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__CURRENCY}`: {
      return immutablySetField(state, 'currency', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__ENGINE}`: {
      return immutablySetField(state, 'engine', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__DRIVE}`: {
      return immutablySetField(state, 'drive', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__GEARBOX}`: {
      return immutablySetField(state, 'gearbox', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__MODIFICATION}`: {
      return immutablySetField(state, 'modification', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__EQUIPMENT}`: {
      return immutablySetField(state, 'equipment', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__VIDEO}`: {
      return immutablySetField(state, 'video', action.payload || null);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.ADD__PHOTOS}`: {
      return immutablySetField(state, 'photos', [...state.photos, action.payload]);
    }
    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.REMOVE__PHOTOS}`: {
      return immutablySetField(state, 'photos', state.photos.filter(p => p.id !== action.payload));
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__COLOR}`: {
      return immutablySetField(state, 'color', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__LOCATION}`: {
      return immutablySetField(state, 'location', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__ABROAD}`: {
      return immutablySetField(state, 'abroad', !!action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__DRIVEN_FROM}`: {
      return immutablySetField(state, 'drivenFrom', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__CONTACT_NAME}`: {
      return immutablySetField(state, 'contactName', action.payload || null);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__EMAIL}`: {
      return immutablySetField(state, 'email', action.payload || null);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__COLOR_FEATURES}`: {
      return immutablySetField(
        state,
        'colorFeatures',
        arrayToPicked(action.payload.map(item => item.id)),
      );
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__PHONES}`: {
      return immutablySetField(
        state,
        'phones',
        Array.isArray(action.payload) ? action.payload : state.phones,
      );
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__OPTIONS}`: {
      const newOptions = { ...state.options };

      Object.keys(action.payload).forEach(key => {
        // Probably should write that in an immutable way
        delete newOptions[key];
        if (action.payload[key]) {
          newOptions[key] = action.payload[key];
        }
      });

      return {
        ...state,
        options: newOptions,
      };
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.CLEAR__OPTIONS}`: {
      return immutablySetField(state, 'options', initialState.selected.options);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__MILEAGE}`: {
      return {
        ...state,
        mileage: parseDecimal(action.payload || null, 5, 0),
      };
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__VIN}`: {
      return {
        ...state,
        vin: action.payload ? `${action.payload}`.toUpperCase() : null,
      };
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__DESCRIPTION}`: {
      return {
        ...state,
        description: action.payload || null,
      };
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__PRICE}`: {
      return {
        ...state,
        price: parseInt(action.payload, 10) || null,
      };
    }

    // Clarify price
    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__CLARIFY_PRICE_LINK}`: {
      return immutablySetField(state, 'clarifyPriceLink', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__CLARIFY_PRICE}`: {
      return immutablySetField(state, 'clarifyPrice', !!action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.TOGGLE__CLARIFY_PRICE}`: {
      return {
        ...state,
        clarifyPrice: !state.clarifyPrice,
        clarifyPriceLink: '',
      };
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.TOGGLE__COLOR_FEATURES}`: {
      const { colorFeatures } = state;

      return {
        ...state,
        colorFeatures: { ...colorFeatures, [action.payload]: !colorFeatures[action.payload] },
      };
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.TOGGLE__STATUSES}`: {
      return {
        ...state,
        statuses: { ...state.statuses, [action.payload]: !state.statuses[action.payload] },
      };
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.TOGGLE__ADVERT_FIELDS}`: {
      const { advertFields } = state;

      return {
        ...state,
        advertFields: { ...advertFields, [action.payload]: !advertFields[action.payload] },
      };
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.ADD__PHONE}`: {
      const phone = action.payload;
      const { phones } = state;

      return {
        ...state,
        phones: phones.includes(phone) ? phones : [...phones, phone],
      };
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.REMOVE__PHONE}`: {
      return {
        ...state,
        phones: state.phones.filter(phone => phone !== action.payload),
      };
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__MODEL_TITLE}`: {
      return immutablySetField(state, 'modelTitle', action.payload || null);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__MAKE_TITLE}`: {
      return immutablySetField(state, 'makeTitle', action.payload || null);
    }

    // Moto

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__CATEGORY}`: {
      return immutablySetField(state, 'category', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__ENGINE_ORDER}`: {
      return immutablySetField(state, 'engineOrder', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__TACTS}`: {
      return immutablySetField(state, 'tacts', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__CAPACITY}`: {
      return immutablySetDecimalField(state, 'capacity', parseFloat(action.payload) || null, 5, 1);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__CAPACITY_UNIT}`: {
      return immutablySetField(state, 'capacityUnit', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__POWER}`: {
      return immutablySetDecimalField(state, 'power', parseFloat(action.payload) || null, 5, 2);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__POWER_UNIT}`: {
      return immutablySetField(state, 'powerUnit', action.payload);
    }

    // Truck

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__CABIN}`: {
      return immutablySetField(state, 'cabin', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__UNLOADING}`: {
      return immutablySetField(state, 'unloading', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__ECOTYPE}`: {
      return immutablySetField(state, 'ecotype', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__CABIN_SUSPENSION}`: {
      return immutablySetField(state, 'cabinSuspension', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__CHASSIS_SUSPENSION}`: {
      return immutablySetField(state, 'chassisSuspension', action.payload);
    }

    // Bus

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__SEATS}`: {
      return immutablySetField(state, 'seats', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__RANGE_TYPE}`: {
      return immutablySetField(state, 'rangeTypes', action.payload);
    }

    // Technics

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__TONNAGE}`: {
      return immutablySetDecimalField(state, 'tonnage', parseFloat(action.payload) || null, 2, 2);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__CAPACITY_BODY}`: {
      return immutablySetDecimalField(
        state,
        'capacityBody',
        parseFloat(action.payload) || null,
        5,
        2,
      );
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__AXISES}`: {
      return immutablySetField(state, 'axises', parseInt(action.payload, 10) || null);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__DIMENSION_WIDTH}`: {
      return immutablySetDecimalField(
        state,
        'dimensionWidth',
        parseFloat(action.payload) || null,
        3,
        2,
      );
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__DIMENSION_HEIGHT}`: {
      return immutablySetDecimalField(
        state,
        'dimensionHeight',
        parseFloat(action.payload) || null,
        3,
        2,
      );
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__DIMENSION_LENGTH}`: {
      return immutablySetDecimalField(
        state,
        'dimensionLength',
        parseFloat(action.payload) || null,
        3,
        2,
      );
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__DIMENSION_WEIGHT}`: {
      return immutablySetDecimalField(
        state,
        'dimensionWeight',
        parseFloat(action.payload) || null,
        3,
        2,
      );
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__CAPACITY_BUCKET}`: {
      return immutablySetDecimalField(
        state,
        'capacityBucket',
        parseFloat(action.payload) || null,
        3,
        2,
      );
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__LAYING_WIDTH}`: {
      return immutablySetDecimalField(
        state,
        'layingWidth',
        parseFloat(action.payload) || null,
        3,
        2,
      );
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__LIFT_HEIGHT}`: {
      return immutablySetDecimalField(
        state,
        'liftHeight',
        parseFloat(action.payload) || null,
        3,
        2,
      );
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__ADMISSION_HEIGHT}`: {
      return immutablySetDecimalField(
        state,
        'admissionHeight',
        parseFloat(action.payload) || null,
        3,
        2,
      );
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__ARM_BOOM}`: {
      return immutablySetDecimalField(state, 'armBoom', parseFloat(action.payload) || null, 3, 2);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__DEP_BIG}`: {
      return immutablySetDecimalField(state, 'depBig', parseFloat(action.payload) || null, 3, 2);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__PRODUCTIVITY}`: {
      return immutablySetDecimalField(
        state,
        'productivity',
        parseFloat(action.payload) || null,
        7,
        2,
      );
    }

    // Trailers

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__CAPACITY_BODY_TYPE}`: {
      return immutablySetField(state, 'capacityBodyType', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__SUSPENSION}`: {
      return immutablySetField(state, 'suspension', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__BRAKES}`: {
      return immutablySetField(state, 'brakes', action.payload);
    }

    // Water

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__OVERALL_LENGTH}`: {
      return immutablySetDecimalField(
        state,
        'overallLength',
        parseFloat(action.payload) || null,
        3,
        2,
      );
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__DRY_WEIGHT}`: {
      return immutablySetDecimalField(state, 'dryWeight', parseFloat(action.payload) || null, 3, 3);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__DRAFT}`: {
      return immutablySetDecimalField(state, 'draft', parseFloat(action.payload) || null, 3, 2);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__DISPLACEMENT}`: {
      return immutablySetDecimalField(
        state,
        'displacement',
        parseFloat(action.payload) || null,
        7,
        1,
      );
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__FUEL_TANK}`: {
      return immutablySetDecimalField(state, 'fuelTank', parseFloat(action.payload) || null, 3, 2);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__WATER_TANK}`: {
      return immutablySetDecimalField(state, 'waterTank', parseFloat(action.payload) || null, 3, 2);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__SEAWORTHINESS_CLASS}`: {
      return immutablySetField(state, 'seaworthinessClass', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__REG_NUMBER}`: {
      return immutablySetField(state, 'regNumber', action.payload);
    }

    case `${ADVERT_FORM_SELECTED}${ADVERT_FORM.SELECTED.SET__USED_WARRANTY}`: {
      return immutablySetField(state, 'usedWarranty', action.payload);
    }

    default:
      return state;
  }
};

const isEdit = (state = initialState.isEdit, action) => {
  switch (action.type) {
    case ADVERT_FORM.SET_IS_EDIT:
      return !!action.payload;

    default:
      return state;
  }
};

const isClearBtn = (state = initialState.isClearBtn, action) => {
  switch (action.type) {
    case ADVERT_FORM.SET_IS_CLEAR_BTN:
      return !!action.payload;

    default:
      return state;
  }
};

export default combineReducers({
  selected,
  data,
  vocabulary,
  isEdit,
  isClearBtn,
});



// WEBPACK FOOTER //
// ./src/shared/reducers/advertForm.js